<template>
  <div class="shape-builder">
    <LevelComplete v-if="isGameComplete" />
    <div v-else>
      <div class="house-template">
        <!-- Target shapes on the house template -->
        <div
          v-for="(target, index) in targets"
          :key="index"
          class="target-shape"
          :data-index="index"
          :class="target.shape.toLowerCase() + '-target'"
          :style="getTargetStyle(target)"
          @click="speakOutLoud(target.shape)"
        />
      </div>
      <p
        v-if="message"
        class="message"
      >
        {{ message }}
      </p>
      <div class="draggable-shapes">
        <!-- Draggable shapes in the palette -->
        <div
          v-for="(shape, index) in shapes"
          :key="index"
          class="shape-draggable"
          :class="shape.shape.toLowerCase()"
          :style="getPaletteShapeStyle(shape, index)"
          @mousedown="startDrag($event, shape)"
          @touchstart="startDrag($event, shape)"
          @click="speakOutLoud(shape.shape)"
        >
          <span class="shape-label">{{ shape.shape }}</span>
        </div>
      </div>
      <!-- The shape being dragged -->
      <div
        v-if="dragging && draggingShape"
        class="shape-draggable dragging"
        :class="draggingShape.shape.toLowerCase()"
        :style="getDraggingShapeStyle()"
      >
        <span class="shape-label">{{ draggingShape.shape }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import LevelComplete from "../LevelComplete.vue";import { speechMixin } from "@/mixins/speechMixin.js";

export default {
  components: {
    LevelComplete,
  },
  mixins: [speechMixin],
  data() {
    return {
      targets: [
        {
          id: 1,
          shape: "Rectangle", // House base
          position: { left: "50%", top: "60%" },
          placed: false,
        },
        {
          id: 2,
          shape: "Triangle", // Roof
          position: { left: "50%", top: "35%" },
          placed: false,
        },
        {
          id: 3,
          shape: "Square", // Door
          position: { left: "50%", top: "65%" },
          placed: false,
        },
        {
          id: 4,
          shape: "Square", // Left window
          position: { left: "40%", top: "55%" },
          placed: false,
        },
        {
          id: 5,
          shape: "Square", // Right window
          position: { left: "60%", top: "55%" },
          placed: false,
        },
        {
          id: 6,
          shape: "Circle", // Sun
          position: { left: "80%", top: "20%" },
          placed: false,
        },
      ],
      shapes: [
        { id: 1, shape: "Rectangle" },
        { id: 2, shape: "Triangle" },
        { id: 3, shape: "Square" },
        { id: 4, shape: "Circle" },
      ],
      shapeColors: {
        Triangle: "#FF5733", // Roof color
        Square: "magenta", // Window color
        Rectangle: "blue", // House base and door color
        Circle: "#FFD700", // Sun color
      },
      message: "",
      selectedVoice: null,
      isGameComplete: false,
      draggingShape: null,
      dragging: false,
    };
  },
  mounted() {
    window.speechSynthesis.onvoiceschanged = this.setupVoices;
    this.setupVoices();
  },
  methods: {
    startDrag(event, shape) {
      event.preventDefault();
      // Create a copy of the shape for dragging
      this.draggingShape = { ...shape };
      this.dragging = true;

      // Get the initial touch/click position
      let clientX, clientY;
      if (event.type === "mousedown") {
        clientX = event.clientX;
        clientY = event.clientY;
      } else if (event.type === "touchstart") {
        clientX = event.touches[0].clientX;
        clientY = event.touches[0].clientY;
      }

      // Set the initial position of the dragging shape
      this.draggingShape.position = {
        left: clientX + "px",
        top: clientY + "px",
      };

      // Attach event listeners to track dragging
      document.addEventListener("mousemove", this.onDragging);
      document.addEventListener("mouseup", this.stopDrag);
      document.addEventListener("touchmove", this.onDragging);
      document.addEventListener("touchend", this.stopDrag);
    },
    onDragging(event) {
      if (!this.dragging) return;
      event.preventDefault();
      let clientX, clientY;
      if (event.type === "mousemove") {
        clientX = event.clientX;
        clientY = event.clientY;
      } else if (event.type === "touchmove") {
        clientX = event.touches[0].clientX;
        clientY = event.touches[0].clientY;
      }

      // Update the position of the dragging shape
      this.draggingShape.position.left = clientX + "px";
      this.draggingShape.position.top = clientY + "px";
    },
    stopDrag(event) {
      if (!this.dragging) return;
      event.preventDefault();

      let clientX, clientY;
      if (event.type === "mouseup") {
        clientX = event.clientX;
        clientY = event.clientY;
      } else if (event.type === "touchend") {
        clientX = event.changedTouches[0].clientX;
        clientY = event.changedTouches[0].clientY;
      }

      // Remove event listeners
      document.removeEventListener("mousemove", this.onDragging);
      document.removeEventListener("mouseup", this.stopDrag);
      document.removeEventListener("touchmove", this.onDragging);
      document.removeEventListener("touchend", this.stopDrag);
      this.dragging = false;

      // Check if dropped over a target
      const droppedElement = document.elementFromPoint(clientX, clientY);
      const targetElement = droppedElement.closest(".target-shape");
      if (targetElement) {
        const targetIndex = targetElement.dataset.index;
        const target = this.targets[targetIndex];
        if (this.draggingShape.shape === target.shape && !target.placed) {
          target.placed = true;
          this.speakOutLoud(`${target.shape}`);
          this.playSound(
            "https://commondatastorage.googleapis.com/codeskulptor-assets/week7-brrring.m4a"
          );
          // Check if game is complete
          if (this.targets.every((t) => t.placed)) {
            this.speakOutLoud("Great Job!")
            this.isGameComplete = true;
            setTimeout(() => {
              this.$router.push("/shapes");
            }, 4000);
          }
        } else {
          this.message = "Oops! Try again.";
          setTimeout(() => {
            this.message = "";
          }, 2000);
        }
      } else {
        this.message = "Drop the shape on the house!";
        setTimeout(() => {
          this.message = "";
        }, 2000);
      }

      // Reset dragging shape
      this.draggingShape = null;
    },
    getTargetStyle(target) {
      let style = {
        ...target.position,
        position: "absolute",
        transform: "translate(-50%, -50%)",
      };

      if (target.shape === "Triangle") {
        // Roof
        style.width = "0";
        style.height = "0";
        style.borderLeft = "75px solid transparent";
        style.borderRight = "75px solid transparent";
        style.borderBottom = `100px solid ${
          target.placed ? this.shapeColors[target.shape] : "#808080"
        }`;
      } else if (target.shape === "Square") {
        // Windows and door
        style.width = "40px";
        style.height = "40px";
        style.backgroundColor = target.placed
          ? this.shapeColors[target.shape]
          : "lightgray";
      } else if (target.shape === "Rectangle") {
        // House base
        style.width = "150px";
        style.height = "100px";
        style.backgroundColor = target.placed
          ? this.shapeColors[target.shape]
          : "#808080";
      } else if (target.shape === "Circle") {
        // Sun
        style.width = "60px";
        style.height = "60px";
        style.backgroundColor = target.placed
          ? this.shapeColors[target.shape]
          : "#808080";
        style.borderRadius = "50%";
      }

      return style;
    },
    getPaletteShapeStyle(shape) {
      let style = {
        position: "relative",
        margin: "10px",
        cursor: "grab",
      };

      const color = this.shapeColors[shape.shape];

      if (shape.shape === "Triangle") {
        style.width = "0";
        style.height = "0";
        style.borderLeft = "30px solid transparent";
        style.borderRight = "30px solid transparent";
        style.borderBottom = `50px solid ${color}`;
      } else if (shape.shape === "Square") {
        style.width = "50px";
        style.height = "50px";
        style.backgroundColor = color;
      } else if (shape.shape === "Rectangle") {
        style.width = "80px";
        style.height = "50px";
        style.backgroundColor = color;
      } else if (shape.shape === "Circle") {
        style.width = "60px";
        style.height = "60px";
        style.backgroundColor = color;
        style.borderRadius = "50%";
      }

      return style;
    },
    getDraggingShapeStyle() {
      let style = {
        position: "absolute",
        left: this.draggingShape.position.left,
        top: this.draggingShape.position.top,
        cursor: "grabbing",
        transform: "translate(-50%, -50%)",
        zIndex: 1000,
      };

      const color = this.shapeColors[this.draggingShape.shape];

      if (this.draggingShape.shape === "Triangle") {
        style.width = "0";
        style.height = "0";
        style.borderLeft = "75px solid transparent";
        style.borderRight = "75px solid transparent";
        style.borderBottom = `100px solid ${color}`;
      } else if (this.draggingShape.shape === "Square") {
        style.width = "50px";
        style.height = "50px";
        style.backgroundColor = color;
      } else if (this.draggingShape.shape === "Rectangle") {
        style.width = "150px";
        style.height = "100px";
        style.backgroundColor = color;
      } else if (this.draggingShape.shape === "Circle") {
        style.width = "60px";
        style.height = "60px";
        style.backgroundColor = color;
        style.borderRadius = "50%";
      }

      return style;
    },
    playSound(url) {
      const audio = new Audio(url);
      audio.play();
    },
    resetGame() {
      // Reset targets
      this.targets.forEach((target) => {
        target.placed = false;
      });
      this.isGameComplete = false;
    },
  },
};
</script>

<style scoped>
.shape-builder {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.house-template {
  position: relative;
  width: 90%;
  max-width: 400px;
  height: 500px;
  background-color: lightblue; /* Sky blue background */
  margin-bottom: 20px;
  margin: auto;
}

.target-shape {
  position: absolute;
}

.draggable-shapes {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
}

.shape-draggable {
  cursor: grab;
  padding: 0;
  transition: transform 0.3s;
  position: relative;
}

.dragging {
  pointer-events: none;
}

.shape-draggable .shape-label {
  position: absolute;
  width: 100%;
  text-align: center;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 1rem;
  font-weight: bold;
  color: #ffffff;
}

/* Shape specific styles */

/* Triangle */
.triangle {
}

/* Square */
.square {
}

/* Rectangle */
.rectangle {
}

/* Circle */
.circle {
  border-radius: 50%;
}

/* Messages */
.message {
  font-size: 2rem;
  font-weight: bold;
  text-align: center;
  color: red;
}
</style>
